/**
 * Whether the user is moving the canvas or something on it right now: a
 * pointer is held down (dragging a card, panning, drawing a selection) or a
 * wheel or trackpad scroll happened a moment ago. Camera moves the user did
 * not ask for (Partnr panning to its ghosts) check it so they never fight a
 * hand that is already moving the view.
 *
 * Listeners are window-wide and reference counted: the first watcher adds
 * them, the last one removes them.
 */

/** A wheel or trackpad scroll this recent still counts as panning. */
const WHEEL_SETTLE_MS = 400;

const pressed = new Set<number>();
let lastWheelAt = -Infinity;
let watchers = 0;

const onDown = (e: PointerEvent) => pressed.add(e.pointerId);
const onUp = (e: PointerEvent) => pressed.delete(e.pointerId);
const onWheel = () => {
  lastWheelAt = performance.now();
};
// A release outside the window (or over an iframe) never arrives; the next
// move with no button held, or losing focus, clears it
const onMove = (e: PointerEvent) => {
  if (e.buttons === 0 && pressed.size > 0) pressed.clear();
};
const onBlur = () => pressed.clear();

/** Starts watching; returns the function that stops. */
export function watchCanvasInteraction(): () => void {
  if (typeof window === 'undefined') return () => {};
  if (watchers++ === 0) {
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('pointerup', onUp, true);
    window.addEventListener('pointercancel', onUp, true);
    window.addEventListener('pointermove', onMove, { capture: true, passive: true });
    window.addEventListener('wheel', onWheel, { capture: true, passive: true });
    window.addEventListener('blur', onBlur);
  }
  let stopped = false;
  return () => {
    if (stopped) return;
    stopped = true;
    if (--watchers > 0) return;
    window.removeEventListener('pointerdown', onDown, true);
    window.removeEventListener('pointerup', onUp, true);
    window.removeEventListener('pointercancel', onUp, true);
    window.removeEventListener('pointermove', onMove, true);
    window.removeEventListener('wheel', onWheel, true);
    window.removeEventListener('blur', onBlur);
    pressed.clear();
  };
}

/** True while a pointer is down, or for a moment after a wheel or trackpad scroll. */
export function isUserMovingCanvas(now: number = performance.now()): boolean {
  return pressed.size > 0 || now - lastWheelAt < WHEEL_SETTLE_MS;
}

/** Test hook. */
export function resetCanvasInteraction(): void {
  pressed.clear();
  lastWheelAt = -Infinity;
}
